/**
 * ErrorBoundary raiz — captura erros não tratados na árvore de componentes
 * e exibe uma tela de fallback sem expor stack traces ao usuário (DD-010).
 *
 * Telemetria: quando `onReportError` for fornecido, é chamado com um
 * `ErrorReport` que NÃO contém:
 *  - stack trace JavaScript (evita exposição de paths do bundle);
 *  - PII (email, token, CPF etc.);
 *  - mensagem de erro completa (apenas os primeiros 200 caracteres).
 *
 * O `componentStack` é truncado a 300 caracteres para orientação de debug
 * sem vazar código de produção de forma excessiva.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import styles from "./ErrorBoundary.module.css";

/** Payload de erro enviado ao sink de telemetria (sem PII/token/stack). */
export interface ErrorReport {
  /** ID único gerado para este evento de erro (formato `err-<timestamp>`). */
  errorId: string;
  /** Nome da classe do erro (ex.: "TypeError"). Nunca contém PII. */
  errorName: string;
  /** Mensagem do erro truncada (máx. 200 caracteres). Pode conter info técnica. */
  errorMessage: string;
  /**
   * Primeiros 300 caracteres do `componentStack` do React — útil para
   * identificar o componente sem expor o stack JS completo.
   */
  componentStack?: string;
  /** Timestamp ISO 8601 do momento do erro. */
  timestamp: string;
}

interface Props {
  children: ReactNode;
  /** Fallback customizado. Se não fornecido, usa o padrão. */
  fallback?: ReactNode;
  /**
   * Sink de telemetria chamado quando um filho lança erro de render.
   * O payload não contém token, PII nem stack trace completo (RNF 6.2, DD-010).
   */
  onReportError?: (report: ErrorReport) => void;
}

interface State {
  hasError: boolean;
  errorId: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorId: null };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true, errorId: `err-${Date.now()}` };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Loga apenas nome + primeiros 200 chars do componentStack (sem PII/token).
    console.error("[ErrorBoundary]", error.name, info.componentStack?.slice(0, 200));

    if (this.props.onReportError) {
      const truncatedStack = info.componentStack?.slice(0, 300);
      const report: ErrorReport = {
        errorId: this.state.errorId ?? `err-${Date.now()}`,
        errorName: error.name,
        // Trunca a mensagem para evitar vazar dados sensíveis acidentais.
        errorMessage: error.message.slice(0, 200),
        // Trunca componentStack — identifica o componente sem expor o stack JS.
        ...(truncatedStack !== undefined ? { componentStack: truncatedStack } : {}),
        timestamp: new Date().toISOString(),
      };
      this.props.onReportError(report);
    }
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className={styles.container} role="alert" aria-live="assertive">
          <div className={styles.icon} aria-hidden="true">!</div>
          <h1 className={styles.title}>Algo deu errado</h1>
          <p className={styles.message}>
            Ocorreu um erro inesperado. Tente recarregar a página. Se o problema
            persistir, entre em contato com o suporte.
          </p>
          {this.state.errorId && (
            <span className={styles.errorId}>Código: {this.state.errorId}</span>
          )}
          <button
            type="button"
            className={styles.button}
            onClick={() => window.location.reload()}
          >
            Recarregar página
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
